import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { concatMap, mergeMap } from 'rxjs/operators';
import { isEoeOrPunchout } from '../../common/constants/pathVariables.js';
import { findCatalogById, getPublishedCatalogs } from '../../common/utils/catalogUtils.js';
import { getVirtualCatalogsPrivateMethod } from '../../generated/api/uiApiMethods.js';
import {
  TypeKeys,
  catalogToValidationRules,
  clearCartItems,
  loadVirtualCatalogsFailed,
  loadVirtualCatalogsFulfilled,
  processCartItems,
  setSelectedVirtualCatalog,
  updateSelectedBaId,
} from '../actions/index.js';
import { getCatalogCode, getShoppingCart, removeCatalogCode, setCatalogCode } from '../common/localStorageUtils.js';
import { callUiApi, prepareUiApiRequest } from '../common/uiApiUtils.js';
import { actionToActionState } from './epicUtils.js';
import type { ActionAndState, EpicDependencies } from './epicUtils.js';
import type { Catalog, VirtualCatalogResponse } from '../../generated/api/models.js';
import type {
  ActionTypes,
  LoadVirtualCatalogsAction,
  LoadVirtualCatalogsFulfilledAction,
  SetSelectedVirtualCatalogAction,
} from '../actions/index.js';
import type { State } from '../common/store.js';
import type { Action } from 'redux';
import type { ActionsObservable, Epic, StateObservable } from 'redux-observable';

export const setSelectedVirtualCatalogEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>
) =>
  action$.ofType(TypeKeys.SET_SELECTED_VIRTUAL_CATALOG).pipe(
    mergeMap((action: SetSelectedVirtualCatalogAction) => {
      action.selectedCatalogCode ? setCatalogCode(action.selectedCatalogCode) : removeCatalogCode();
      const selectedCatalog: Catalog | undefined = findCatalogById(
        state$.value.selfservice?.virtualCatalogs?.items,
        action.selectedCatalogCode
      );
      if (selectedCatalog?.billingAccountId != null) {
        return of(updateSelectedBaId(selectedCatalog?.billingAccountId));
      }

      return EMPTY;
    })
  );

//
// Virtual Catalogs
//

const loadVirtualCatalogsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(action$.pipe(ofType(TypeKeys.LOAD_VIRTUAL_CATALOGS)), (action: LoadVirtualCatalogsAction) =>
    actionToActionState(action, state$, 'virtualCatalogs')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      if (!actionAndState.state || !actionAndState.state.query) {
        throw new Error('invalid action state for loading virtual catalogs: query missing');
      }
      const loadCatalogsAction = actionAndState.action as LoadVirtualCatalogsAction;
      return callUiApi({
        epicDependencies,
        failureAction: loadVirtualCatalogsFailed,
        method: getVirtualCatalogsPrivateMethod(),
        state$,
        successAction: (response: VirtualCatalogResponse) =>
          loadVirtualCatalogsFulfilled(response, loadCatalogsAction.catalogPreSelected),
      });
    })
  );

export const loadVirtualCatalogsFulfilledEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>
) =>
  action$.ofType(TypeKeys.LOAD_VIRTUAL_CATALOGS_FULFILLED).pipe(
    mergeMap((action: LoadVirtualCatalogsFulfilledAction) => {
      const publishedCatalogs = getPublishedCatalogs(state$.value.selfservice?.virtualCatalogs?.items);
      let selectedCatalogCode;
      if (
        action.catalogPreSelected &&
        publishedCatalogs &&
        publishedCatalogs.length === 1 &&
        publishedCatalogs[0].catalogCode
      ) {
        selectedCatalogCode = publishedCatalogs[0].catalogCode;
      }
      if (!selectedCatalogCode) {
        selectedCatalogCode = getCatalogCode();
      }
      if (!selectedCatalogCode) {
        return of(clearCartItems(), setSelectedVirtualCatalog(null));
      }
      const selectedCatalog = findCatalogById(action.catalogs, selectedCatalogCode);
      if (!selectedCatalog) {
        return of(clearCartItems(), setSelectedVirtualCatalog(null));
      }
      if (isEoeOrPunchout(window.location.pathname)) {
        return of(
          processCartItems(false, true, getShoppingCart(), catalogToValidationRules(selectedCatalog)),
          setSelectedVirtualCatalog(selectedCatalogCode)
        );
      }
      return of(setSelectedVirtualCatalog(selectedCatalogCode));
    })
  );

export const catalogsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = combineEpics(
  loadVirtualCatalogsEpic,
  loadVirtualCatalogsFulfilledEpic,
  setSelectedVirtualCatalogEpic
);
